import { useEffect, useState } from 'react';
import { Box } from '@mui/material';
import type { Theme } from '@mui/material/styles';
import axiosNest from '../api/axiosNest';

/**
 * Pasek środowiska akceptacyjnego (staging, decyzja użytkownika 12.10.2026).
 *
 * Staging stoi na kopii bazy produkcyjnej, z kontami pokazowymi i wyłączonymi
 * integracjami (TMS, eco-truck, giełdy, zapis do VIA). Wygląda jak produkcja,
 * więc bez wyraźnego znaku łatwo pomylić karty przeglądarki — w obie strony:
 * pokazać „na produkcji” coś, czego tam nie ma, albo szukać na stagingu
 * zlecenia, które przyszło z TMS po zrobieniu kopii.
 *
 * Pasek pojawia się wyłącznie przy `REACT_APP_ENVIRONMENT=staging` (ustawia go
 * `app-staging-deploy.yml`); dev i prod budują się bez tej zmiennej, nic nie
 * rysują i o nic nie pytają. Dzień kopii bazy zna tylko maszyna — skrypt
 * odświeżania wpisuje go do env API — więc front pyta publiczne
 * `GET /environment` (bez logowania: pasek stoi też na ekranie logowania).
 * Brak odpowiedzi, pustej ani nieczytelnej daty nie zgadujemy — pasek mówi,
 * że jej nie podano.
 */

export const ENVIRONMENT_URL = '/environment';

export const STAGING_ENVIRONMENT = 'staging';

/** Czy ta paczka frontu jest zbudowana dla środowiska akceptacyjnego. */
export function isStagingEnvironment(environment: string | undefined): boolean {
  return (environment ?? '').trim() === STAGING_ENVIRONMENT;
}

/**
 * Dzień kopii RRRR-MM-DD → DD.MM.RRRR. Napis, nie `Date` — to data bez
 * godziny i nie ma czego przeliczać na strefę. Nieistniejący dzień
 * (2026-02-30) albo inny format = `null`.
 */
export function formatCopyDate(value: string | undefined): string | null {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec((value ?? '').trim());
  if (!match) return null;
  const [, year, month, day] = match;
  const check = new Date(Date.UTC(Number(year), Number(month) - 1, Number(day)));
  if (
    check.getUTCFullYear() !== Number(year) ||
    check.getUTCMonth() !== Number(month) - 1 ||
    check.getUTCDate() !== Number(day)
  ) {
    return null;
  }
  return `${day}.${month}.${year}`;
}

/**
 * Treść paska; `null` = to nie jest środowisko akceptacyjne, paska nie ma.
 * `copyDate`: `undefined` — odpowiedź jeszcze nie przyszła (bez zdania
 * o dacie), `null` albo zła wartość — daty nie podano.
 */
export function environmentBannerText(
  environment: string | undefined,
  copyDate: string | null | undefined,
): string | null {
  if (!isStagingEnvironment(environment)) return null;
  if (copyDate === undefined) {
    return 'Środowisko akceptacyjne — dane z kopii produkcji, integracje wyłączone';
  }
  const date = formatCopyDate(copyDate ?? undefined);
  const data = date
    ? `dane z kopii produkcji z dnia ${date}`
    : 'dane z kopii produkcji (data kopii nie została podana)';
  return `Środowisko akceptacyjne — ${data}, integracje wyłączone`;
}

/** Dzień kopii z odpowiedzi `GET /environment`; cokolwiek innego = `null`. */
export function copyDateFromResponse(body: unknown): string | null {
  if (!body || typeof body !== 'object') return null;
  const value = (body as { dataCopyDate?: unknown }).dataCopyDate;
  return typeof value === 'string' ? value : null;
}

export const EnvironmentBanner = () => {
  const staging = isStagingEnvironment(process.env.REACT_APP_ENVIRONMENT);
  const [copyDate, setCopyDate] = useState<string | null | undefined>(undefined);
  useEffect(() => {
    if (!staging) return undefined;
    let active = true;
    axiosNest
      .get(ENVIRONMENT_URL)
      .then((res) => {
        if (active) setCopyDate(copyDateFromResponse(res.data));
      })
      .catch(() => {
        if (active) setCopyDate(null);
      });
    return () => {
      active = false;
    };
  }, [staging]);
  const text = environmentBannerText(process.env.REACT_APP_ENVIRONMENT, copyDate);
  if (!text) return null;
  return (
    <Box
      role="status"
      data-testid="environment-banner"
      sx={(theme: Theme) => ({
        backgroundColor: theme.palette.warning.main,
        color: theme.palette.warning.contrastText,
        fontWeight: 600,
        fontSize: '0.875rem',
        textAlign: 'center',
        px: 2,
        py: 0.5,
      })}
    >
      {text}
    </Box>
  );
};

export default EnvironmentBanner;
